import React, { useEffect, useState } from 'react';
import {
  ClipboardList, DollarSign, FileCheck2, Users, PenLine, Repeat, BarChart3, Shield,
} from 'lucide-react';
import StatsCard from './analytics/StatsCard';
import RevenueTrendChart from './analytics/RevenueTrendChart';
import CoverDistributionChart from './analytics/CoverDistributionChart';
import RecentTransactionsCard from './analytics/RecentTransactionsCard';
import QuickActionsCard from './analytics/QuickActionsCard';
import { fetchUnderwritingAnalytics, formatCurrency } from '../services/adminAnalyticsApi';
import { canSeeComprehensiveUnderwritingNav } from '../utils/comprehensiveAccess';
import '../styles/analytics-theme.css';

const UnderwritingOverview = ({ user, onTabChange, onShowNotification }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const result = await fetchUnderwritingAnalytics();
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled && onShowNotification) onShowNotification(error.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [onShowNotification]);

  if (loading) {
    return (
      <div className="ab-page ab-surface-light">
        <div className="ab-empty">Loading underwriting analytics…</div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const recent = (data?.recentCovers || []).map((r) => ({
    ...r,
    insured_person: r.insured_name,
    premium: r.premium,
  }));

  const quickActions = [
    {
      id: 'issue',
      title: 'Issue Cover for Agent',
      description: 'Start a new cover on behalf of an agent',
      icon: PenLine,
      accent: 'primary',
      onClick: () => onTabChange && onTabChange('underwriting-issue-cover'),
    },
    {
      id: 'extend',
      title: 'Buy / Extend Policy',
      description: 'Extend or purchase TPO/TOR for an agent',
      icon: Repeat,
      accent: 'sky',
      onClick: () => onTabChange && onTabChange('underwriting-extend-policy'),
    },
    {
      id: 'covers',
      title: 'View Covers Issued',
      description: 'Full list of covers you have issued',
      icon: ClipboardList,
      accent: 'emerald',
      onClick: () => onTabChange && onTabChange('underwriting-covers-issued'),
    },
    ...(canSeeComprehensiveUnderwritingNav(user)
      ? [
          {
            id: 'comprehensive',
            title: 'Comprehensive',
            description: 'COMP purchase flow — 1 month · 30%',
            icon: Shield,
            accent: 'amber',
            onClick: () => onTabChange && onTabChange('underwriting-comprehensive'),
          },
          {
            id: 'comp-continuations',
            title: 'Extend Comprehensive',
            description: 'Extensions due → email requests@',
            icon: ClipboardList,
            accent: 'emerald',
            onClick: () => onTabChange && onTabChange('underwriting-comp-continuations'),
          },
        ]
      : []),
  ];

  return (
    <div className="ab-page ab-surface-light">
      <div className="ab-page-header">
        <div>
          <h1 className="ab-page-title">Underwriting Dashboard</h1>
          <p className="ab-page-subtitle">Track covers issued, premiums, and certificate status</p>
        </div>
      </div>

      <div className="ab-stats-grid ab-stats-grid-5">
        <StatsCard
          title="Total Covers Issued"
          value={Number(stats.total_covers || 0).toLocaleString()}
          subtitle="all time"
          icon={ClipboardList}
          variant="primary"
        />
        <StatsCard
          title="Total Premium"
          value={formatCurrency(stats.total_premium)}
          subtitle="lifetime value"
          icon={DollarSign}
        />
        <StatsCard
          title="This Month"
          value={formatCurrency(stats.month_premium)}
          subtitle={`${Number(stats.month_covers || 0).toLocaleString()} covers`}
          icon={BarChart3}
        />
        <StatsCard
          title="Certificates Issued"
          value={Number(stats.certificates_issued || 0).toLocaleString()}
          subtitle={`${Number(stats.pending_certificates || 0).toLocaleString()} pending`}
          icon={FileCheck2}
        />
        <StatsCard
          title="Agent vs Direct"
          value={`${Number(stats.agent_linked || 0).toLocaleString()} / ${Number(stats.direct_business || 0).toLocaleString()}`}
          subtitle="agent-linked / direct business"
          icon={Users}
        />
      </div>

      <div className="ab-charts-row two-thirds" style={{ marginBottom: '1.5rem' }}>
        <RevenueTrendChart trendData={data?.monthlyTrend || []} />
        <CoverDistributionChart distribution={data?.distribution || []} />
      </div>

      <div className="ab-dashboard-bottom">
        <RecentTransactionsCard
          items={recent}
          onViewAll={() => onTabChange && onTabChange('underwriting-covers-issued')}
          emptyMessage="No covers issued yet. Use Issue Cover to get started."
        />
        <QuickActionsCard actions={quickActions} />
      </div>
    </div>
  );
};

export default UnderwritingOverview;
